import React, { useEffect } from "react";

import { stepCompleted } from "EmployerApplication/Actions";
import { quotePageName } from "QuotePage/urls";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import _, { isEmpty } from "underscore";
import { validateRequiredSignature } from "Utils/Validations";

import {
  useGetCurrentCompanyApplicationQuery,
  useUpdateCompanyApplicationMutation,
  useUpdateCompanyPolicyMutation,
  useSignApplicationMutation,
  useGetCompanyContributionsQuery,
} from "../api";
import { AdditionalPlansSection, ShoppingCartSection, CoverageStartDateSection, ContributionAmountSection, ApplicationFormPage } from "../Components";
import { AddDentalModal } from "../Components/shoppingCartSection/AddDentalModal";
import { AddVisionModal } from "../Components/shoppingCartSection/AddVisionModal";
import LifePlansModal from "../Components/shoppingCartSection/LifePlansModal";
import LongTermDisabilityPlansModal from "../Components/shoppingCartSection/LongTermDisabilityPlansModal";
import ShortTermDisabilityPlansModal from "../Components/shoppingCartSection/ShortTermDisabilityPlansModal";
import { useDisabilityEnabled } from "../hooks";
import { ErrorHandler } from "Cobalt/ErrorHandler";
import { useModal } from "Cobalt/Modal";
import { InputSkeleton } from "Cobalt/Skeleton";
import Stack from "Cobalt/Stack";
import BodyText from "Common/BodyText";
import { getCart } from "Common/CartService";
import { CONTRIBUTION_TYPES, MIN_TIERED_CONTRIBUTIONS } from "Common/Constants";
import { SIFinalFormField } from "Common/forms";
import SignatureForm from "Common/Signature";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import { SurfaceSection } from "Theme/Surface";

const { percentage: PERCENTAGE_CONTRIBUTION, fixed: FIXED_CONTRIBUTION, percent_of_base_plan: PERCENT_OF_BASE_PLAN_CONTRIBUTION } = CONTRIBUTION_TYPES;

export const CoverageOptionsPageContext = React.createContext({
  isDentalModalShowing: false,
  showDentalModal: () => {},
  isVisionModalShowing: false,
  showVisionModal: () => {},
  isLifeModalShowing: false,
  showLifeModal: () => {},
});

export const companyContributionInitialValues = (companyContributions: any[]) => {
  const contribution = companyContributions?.[0];
  return {
    employee_percentage: contribution && contribution.employeePercentage != null ? contribution.employeePercentage : 50,
    dependent_percentage: contribution && contribution.dependentPercentage != null ? contribution.dependentPercentage : 0,
    employee_fixed_amount: contribution ? contribution.employeeFixedAmount : null,
    dependent_fixed_amount: contribution ? contribution.dependentFixedAmount : null,
  };
};

export const tieredContributions = (companyContributions: any[]) => {
  if (!companyContributions || companyContributions.length === 0) return [];
  return _.values(companyContributions).filter((c: any) => c.role !== null);
};

export const tieredContributionInitialValues = (companyContributions: any[]) => {
  const initial = tieredContributions(companyContributions);
  const defaultContribution = {
    role: "full_time",
    employeePercentage: "50",
    dependentPercentage: "0",
    employeeFixedAmount: null,
    dependentFixedAmount: null,
  };

  const emptyContribution = {
    role: null,
    employeePercentage: null,
    dependentPercentage: null,
    employeeFixedAmount: null,
    dependentFixedAmount: null,
  };

  if (initial.length === 0) initial.push(defaultContribution);
  while (initial.length < MIN_TIERED_CONTRIBUTIONS) initial.push(emptyContribution);
  return initial;
};

const CoverageOptionsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { data: companyApplication, isLoading: isLoadingApplication } = useGetCurrentCompanyApplicationQuery();

  const policyIdFromStore = useSelector((s: any) => s.companyPolicyId);
  const companyPolicyId = companyApplication?.id ?? policyIdFromStore;

  const { data: companyContributions, isLoading: isLoadingContributions } = useGetCompanyContributionsQuery(companyPolicyId, {
    skip: !companyPolicyId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const { enabled: isDisabilityEnabled, isLoading: isLoadingDisabilityEnabled } = useDisabilityEnabled();

  const cart = useSelector(getCart);
  const medicalCart = cart?.medical?.filter(Boolean) || [];

  const isRenewal = companyApplication?.isRenewalPolicy;
  const carrierMax = companyApplication?.carrierMaxOfferedPlans;
  const employeeProfile = companyApplication?.employeeProfile || [];
  const allEmployeeCosts = companyApplication?.allEmployeeCosts || [];

  const [updateCompanyApplication, { isError: isSubmitError, error: submitError }] = useUpdateCompanyApplicationMutation();
  const [updateCompanyPolicy] = useUpdateCompanyPolicyMutation();
  const [signApplication, { isError: isSignError, error: signError }] = useSignApplicationMutation();

  const contributionsReady = !!companyContributions && !isLoadingContributions;
  const disabilityKnown = !isLoadingDisabilityEnabled; // can be false; we just don't block

  const submitHandler = async (values: any) => {
    const { company_application, company_contribution, signatureImageDataUrl, tiered_contributions } = values;
    const contributionType = company_application?.type_of_contribution;
    await updateCompanyApplication({
      id: companyPolicyId,
      ...company_application,
      ...(company_application?.contribution_method === "tiered" && { tiered_contributions }),
    }).unwrap();

    if (company_application?.contribution_method !== "tiered") {
      let contributionFields;
      if (contributionType === FIXED_CONTRIBUTION) {
        contributionFields = {
          type_of_contribution: contributionType,
          employee_percentage: null,
          dependent_percentage: null,
          employee_fixed_amount: company_contribution.employee_fixed_amount,
          dependent_fixed_amount: company_contribution.dependent_fixed_amount,
        };
      } else if (contributionType === PERCENTAGE_CONTRIBUTION || contributionType === PERCENT_OF_BASE_PLAN_CONTRIBUTION) {
        contributionFields = {
          type_of_contribution: contributionType,
          employee_percentage: company_contribution.employee_percentage,
          dependent_percentage: company_contribution.dependent_percentage,
          employee_fixed_amount: null,
          dependent_fixed_amount: null,
        };
      }

      await updateCompanyPolicy({ id: companyPolicyId, ...contributionFields }).unwrap();
    }

    if (isRenewal) {
      await signApplication({ id: companyPolicyId, signatureImageDataUrl }).unwrap();
    }

    if (isRenewal) {
      dispatch(stepCompleted("coverageOptions"));
    } else {
      history.push("/employer-application/company-information");
    }
  };

  const handleClickBack = () => {
    history.push(appendAuthParams(quotePageName()));
  };

  let initialValues: any = {
    company_application: {},
    company_contribution: {},
    signatureImageDataUrl: "",
    tiered_contributions: [],
    hasPartTimeContribution: false,
  };

  if (!isLoadingApplication && contributionsReady) {
    const hasPartTimeContribution = companyContributions.some((c: any) => c.role === "part_time");
    initialValues = {
      company_application: {
        coverage_start_date_at: companyApplication?.coverageStartDateAt,
        type_of_contribution: CONTRIBUTION_TYPES[companyApplication?.typeOfContribution] || CONTRIBUTION_TYPES.percentage,
        contribution_method: companyApplication?.hasTieredContributions ? "tiered" : "nonTiered",
        probationary_period: companyApplication?.probationaryPeriod,
      },
      company_contribution: companyContributionInitialValues(companyContributions),
      signatureImageDataUrl: companyApplication?.signatureImageDataUrl,
      tiered_contributions: tieredContributionInitialValues(companyContributions),
      hasPartTimeContribution,
    };
  }

  return (
    <ApplicationFormPage
      title={!isRenewal ? "Configure Policy" : undefined}
      agentMessage={
        !isRenewal
          ? "I'm Leigh, your virtual insurance specialist. I'll walk you through your company's health insurance application. The whole process takes about 20 minutes. First, let's have you confirm your plans and finalize your quote."
          : undefined
      }
      isLoading={isLoadingApplication}
      initialValues={initialValues}
      formKey={`${companyPolicyId}:${JSON.stringify(companyContributions)}`}
      onSubmit={submitHandler}
      onBack={handleClickBack}
      saveDisabled={isEmpty(medicalCart) || !contributionsReady}
    >
      <CoverageOptionsPageContent
        isRenewal={isRenewal}
        carrierMax={carrierMax}
        medicalCart={medicalCart}
        employeeProfile={employeeProfile}
        allEmployeeCosts={allEmployeeCosts}
        contributionsReady={contributionsReady}
        disabilityKnown={disabilityKnown}
        isDisabilityEnabled={isDisabilityEnabled}
        companyApplication={companyApplication}
        isSubmitError={isSubmitError}
        submitError={submitError}
        isSignError={isSignError}
        signError={signError}
      />
    </ApplicationFormPage>
  );
};

const CoverageOptionsPageContent = ({
  isRenewal,
  carrierMax,
  medicalCart,
  employeeProfile,
  allEmployeeCosts,
  contributionsReady,
  disabilityKnown,
  isDisabilityEnabled,
  companyApplication,
  isSubmitError,
  submitError,
  isSignError,
  signError,
}: any) => {
  const { isShowing: isDentalModalShowing, show: showDentalModal, hide: hideDentalModal } = useModal();
  const { isShowing: isVisionModalShowing, show: showVisionModal, hide: hideVisionModal } = useModal();
  const { isShowing: isLifeModalShowing, show: showLifeModal, hide: hideLifeModal } = useModal();
  const { isShowing: isLongTermDisabilityModalShowing, show: showLongTermDisabilityModal, hide: hideLongTermDisabilityModal } = useModal();
  const { isShowing: isShortTermDisabilityModalShowing, show: showShortTermDisabilityModal, hide: hideShortTermDisabilityModal } = useModal();

  const payrollContributionSectionProps = {
    employeeProfile,
    allEmployeeCosts,
    typeOfContribution: CONTRIBUTION_TYPES[companyApplication?.typeOfContribution],
  };

  return (
    <>
      <CoverageOptionsPageContext.Provider
        value={{
          isDentalModalShowing,
          showDentalModal,
          isVisionModalShowing,
          showVisionModal,
          isLifeModalShowing,
          showLifeModal,
          isLongTermDisabilityModalShowing,
          showLongTermDisabilityModal,
          isShortTermDisabilityModalShowing,
          showShortTermDisabilityModal,
        }}
      >
        <SurfaceSection>
          <ShoppingCartSection carrierMax={carrierMax} />
        </SurfaceSection>

        {disabilityKnown && isDisabilityEnabled && (
          <SurfaceSection>
            <AdditionalPlansSection />
          </SurfaceSection>
        )}

        {!isEmpty(medicalCart) && (
          <SurfaceSection title="Contribution Amount">
            {contributionsReady ? (
              <ContributionAmountSection isRenewal={isRenewal} payrollContributionSectionProps={payrollContributionSectionProps} />
            ) : (
              <Stack>
                <InputSkeleton type="radio" className="w-7/12" />
                <InputSkeleton type="radio" className="w-6/12" />
                <InputSkeleton type="text" className="w-5/12" />
                <InputSkeleton type="text" className="w-5/12" />
              </Stack>
            )}
          </SurfaceSection>
        )}

        {!isRenewal ? (
          <SurfaceSection title="Coverage Start Date">
            <CoverageStartDateSection companyApplication={companyApplication} />
          </SurfaceSection>
        ) : (
          <SurfaceSection>
            <BodyText mb={4}>
              By signing below, I authorize SimplyInsured to send the provided information to the insurance company for the purpose of renewing coverage.
            </BodyText>
            <SIFinalFormField
              name="signatureImageDataUrl"
              label={null}
              component={SignatureForm}
              componentProps={{ showSubmitButton: false, clearButtonText: "Clear Signature" }}
              compact
              noBounce={false}
              validate={validateRequiredSignature}
            />
            <ErrorHandler
              name="SignatureField"
              action="validate"
              isError={isSubmitError || isSignError}
              error={signError || submitError}
              className="m-4 mt-0 w-full"
            />
          </SurfaceSection>
        )}
      </CoverageOptionsPageContext.Provider>

      <AddDentalModal show={isDentalModalShowing} onHide={hideDentalModal} />
      <AddVisionModal show={isVisionModalShowing} onHide={hideVisionModal} />
      <LifePlansModal show={isLifeModalShowing} onHide={hideLifeModal} />
      {disabilityKnown && isDisabilityEnabled && (
        <>
          <LongTermDisabilityPlansModal show={isLongTermDisabilityModalShowing} onHide={hideLongTermDisabilityModal} />
          <ShortTermDisabilityPlansModal show={isShortTermDisabilityModalShowing} onHide={hideShortTermDisabilityModal} />
        </>
      )}
    </>
  );
};

export default CoverageOptionsPage;
